import React, { useCallback, useEffect, useMemo } from 'react';
import { STATUSES as ROOM_STATUSES } from '../../../../entities/room/enums';
// @ts-ignore
import prevSound from '../../../../../assets/sound/discharge.mp3';
// @ts-ignore
import nextSound from '../../../../../assets/sound/orReady.mp3';
import { throttle } from 'lodash';
import Mousetrap from 'mousetrap';
import { useMutation, useQuery } from '@apollo/client';
import { orTabletProcedureSteps } from '@/graph/procedureSteps';
import { Patient, PatientStatus } from '@/types/Patient';
import { completeCleaning as completeCleaningMutation, setPatientStatus } from '../../../../../graph/rooms';
import { getProcedureState, ProcedureState, toPatientStatus } from '../util/procedureSteps';
import Cleaning from './Cleaning';
import ProcedureHandling from './ProcedureHandling';
import { ProcedureStep } from '@/types/ProcedureStep';
import { Procedure } from '@/types/Procedure';
import { Room } from '@/types/Room';
import get from 'lodash/get';
import { useScope } from '@/hooks/useScope';
import { playSound } from '@/components/pages/kiosk/monitor/hooks/useAlerts';

export const withAlertSound = (sound: any) => (fn: any) => async () => {
  try {
    // we need to play it right away, as Safari prevents autoplay
    playSound(sound, true);
    await fn();
  } catch (e) {
    console.warn('Operation failed. Not playing sound notification');
  }
};

const CurrentProcedure = ({
  nextProcedure,
  roomId,
  room,
  working,
  withWorking,
  nextProceduresInOperationRoom,
  nextRemainingProcedure,
  notifyPhysician,
  loading,
  initialFlipRoomState,
}: {
  nextProcedure: Procedure;
  roomId: number;
  room: Room & { status: string; patient: Patient & { duration?: string } };
  working: boolean;
  withWorking: any;
  nextProceduresInOperationRoom: Procedure[];
  nextRemainingProcedure: Procedure;
  notifyPhysician: () => Promise<void>;
  loading: boolean;
  initialFlipRoomState?: boolean;
}) => {
  const { patient, status } = room;
  const { physician, procedureType, procedure } = patient;

  const withAlertSoundPrev = withAlertSound(prevSound);
  const withAlertSoundNext = withAlertSound(nextSound);

  const [completeCleaning] = useMutation(completeCleaningMutation);
  const [setStatus] = useMutation(setPatientStatus);

  const { data, refetch } = useQuery(orTabletProcedureSteps, {
    variables: {
      patientId: patient?.id,
    },
  });

  useEffect(() => {
    if (!patient?.id) {
      return;
    }

    void refetch();
  }, [patient?.id, room?.patient?.status, room?.procedureStartedAt, refetch]);

  const changeStatus = useCallback(
    async (status: PatientStatus | null) => {
      if (!status) {
        return;
      }
      await setStatus({
        variables: {
          id: patient?.id,
          status,
        },
        refetchQueries: [{ query: orTabletProcedureSteps, variables: { patientId: patient?.id } }],
      });
    },
    [patient?.id, setStatus]
  );

  const updateRoomStatus = useCallback(
    async (id?: number) => {
      if (!id) {
        return;
      }
      await completeCleaning({ variables: { id } });
    },
    [completeCleaning]
  );

  const procedureState: (ProcedureState & { nextType: string | null }) | null = useMemo(() => {
    if (status === ROOM_STATUSES.CLEANING) {
      return {
        steps: null,
        index: null,
        prev: null,
        next: () => updateRoomStatus(roomId),
        nextLabel: 'Cleaning Completed',
        nextType: null,
      };
    }

    const steps = data?.orTabletProcedureSteps || [];
    const state = getProcedureState(steps, status as PatientStatus);
    return {
      steps: steps.map((e: ProcedureStep, i: number) => ({
        ...e,
        highlight: i === (state?.index || 0),
        show: i <= (state?.index || 0),
      })),
      index: state?.index || null,
      prev: state?.prev?.type ? withAlertSoundPrev(() => changeStatus(toPatientStatus(state?.prev?.type))) : null,
      next: state?.next?.type ? withAlertSoundNext(() => changeStatus(toPatientStatus(state?.next?.type))) : null,
      nextLabel: state?.next?.name || null,
      nextType: state?.next?.type || null,
    };
  }, [
    status,
    data?.orTabletProcedureSteps,
    withAlertSoundPrev,
    withAlertSoundNext,
    updateRoomStatus,
    roomId,
    changeStatus,
  ]);

  const scope = useScope();
  const autoPhysicianIn = get(scope, 'hospital.modules.autoPhysicianIn', false);
  const nextType = procedureState?.nextType;
  const physicianRoom = get(physician, 'room.id');
  const procedureRoom = get(procedure, 'orId');
  const next = procedureState?.next;

  useEffect(() => {
    if (autoPhysicianIn && nextType === 'PHYSICIAN_IN' && physicianRoom === procedureRoom) {
      next?.();
    }
  }, [autoPhysicianIn, next, nextType, physicianRoom, procedureRoom]);

  const isCleaning = status === ROOM_STATUSES.CLEANING;

  // forward button handler
  useEffect(() => {
    Mousetrap.bind(
      'l',
      throttle(() => {
        procedureState?.next?.();
      }, 1000)
    );
    return () => {
      Mousetrap.unbind('l');
    };
  }, [procedureState, procedureState.next]);

  // back button handler
  useEffect(() => {
    Mousetrap.bind(
      'k',
      throttle(() => {
        procedureState?.prev?.();
      }, 1000)
    );
    return () => {
      Mousetrap.unbind('k');
    };
  }, [procedureState, procedureState.prev]);

  return (
    <>
      {isCleaning ? (
        <Cleaning
          status={status}
          room={room}
          nextProcedure={nextProcedure}
          procedureState={procedureState}
          working={working}
          withWorking={withWorking}
        />
      ) : (
        <>
          <ProcedureHandling
            status={status}
            room={room}
            physician={physician}
            patient={patient}
            procedureType={procedureType}
            procedureState={procedureState}
            nextProceduresInOperationRoom={nextProceduresInOperationRoom}
            notifyPhysician={notifyPhysician}
            editableAnesthesiologist={true}
            initialFlipRoomState={initialFlipRoomState}
          />
        </>
      )}
    </>
  );
};

export default CurrentProcedure;
